import React, { useState, useRef, useEffect } from 'react';

interface VerificationCodeProps {
  length: number;
  onComplete: (code: string) => void;
  onCodeChange?: (code: string) => void;
  value?: string;
}

export function VerificationCode({ length, onComplete, onCodeChange, value = '' }: VerificationCodeProps) {
  const [code, setCode] = useState<string[]>(new Array(length).fill(''));
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (inputs.current[0]) {
      inputs.current[0].focus();
    }
  }, []);

  // Sync external value with internal state
  useEffect(() => {
    if (value === '' && code.some(c => c !== '')) {
      // Reset the code when value is cleared
      setCode(new Array(length).fill(''));
    } else if (value && value !== code.join('')) {
      // Update code from external value
      const newCode = value.split('').concat(new Array(length).fill('')).slice(0, length);
      setCode(newCode);
    }
  }, [value, length]);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, length).split('');
      const newCode = [...code];
      pastedCode.forEach((char, i) => {
        if (index + i < length) {
          newCode[index + i] = char;
        }
      });
      setCode(newCode);
      onCodeChange?.(newCode.join(''));
      
      // Focus on the next empty input or the last input
      const nextIndex = Math.min(index + pastedCode.length, length - 1);
      inputs.current[nextIndex]?.focus();
      
      if (newCode.every(digit => digit !== '')) {
        onComplete(newCode.join(''));
      }
    } else {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      onCodeChange?.(newCode.join(''));

      // Move to next input
      if (value && index < length - 1) {
        inputs.current[index + 1]?.focus();
      }

      // Check if code is complete
      if (newCode.every(digit => digit !== '') && newCode.join('').length === length) {
        onComplete(newCode.join(''));
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex justify-center space-x-3">
      {code.map((digit, index) => (
        <input
          key={index}
          ref={el => inputs.current[index] = el}
          type="text"
          inputMode="numeric"
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          className="w-14 h-14 text-center text-2xl font-semibold border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:border-transparent transition-all duration-200"
          maxLength={1}
        />
      ))}
    </div>
  );
}